import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  Shield, ArrowLeft, Trophy, Star, Heart,
  RotateCcw, Swords, Castle, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const GrammarGuardianGame = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [gameState, setGameState] = useState('ready');
  const [difficulty, setDifficulty] = useState('medium');
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [health, setHealth] = useState(100);
  const [wave, setWave] = useState(1);
  const [enemies, setEnemies] = useState([]);
  const [currentEnemy, setCurrentEnemy] = useState(null);
  const [selectedFix, setSelectedFix] = useState(null);
  const [enemiesDefeated, setEnemiesDefeated] = useState(0);
  const [combo, setCombo] = useState(0);
  const [castleShake, setCastleShake] = useState(false);

  const [shuffledOptions, setShuffledOptions] = useState([]);

  // Shuffle options when current enemy changes
  useEffect(() => {
    if (currentEnemy) {
      const allOptions = [currentEnemy.correct, ...currentEnemy.options.filter(o => o !== currentEnemy.correct)];
      const shuffled = [...allOptions].sort(() => Math.random() - 0.5);
      setShuffledOptions(shuffled);
      setSelectedFix(null);
    }
  }, [currentEnemy?.id]);

  // Difficulty settings
  const difficultySettings = {
    easy: { health: 150, damage: 15, reachDamage: 20, speed: 0.3, startLevel: 1 },
    medium: { health: 100, damage: 10, reachDamage: 25, speed: 0.5, startLevel: 1 },
    hard: { health: 75, damage: 15, reachDamage: 35, speed: 0.8, startLevel: 2 }
  };

  // Grammar problems by level
  const grammarProblems = {
    1: [
      { sentence: "The cat are sleeping.", error: "are", correct: "is", options: ["is", "were", "am"], rule: "Subject-verb agreement" },
      { sentence: "She don't like pizza.", error: "don't", correct: "doesn't", options: ["doesn't", "do", "didn't"], rule: "Subject-verb agreement" },
      { sentence: "Him went to school.", error: "Him", correct: "He", options: ["He", "His", "They"], rule: "Subject pronouns" },
      { sentence: "I seen the movie.", error: "seen", correct: "saw", options: ["saw", "see", "seeing"], rule: "Past tense" },
      { sentence: "They was happy.", error: "was", correct: "were", options: ["were", "is", "been"], rule: "Subject-verb agreement" },
    ],
    2: [
      { sentence: "Me and him went.", error: "Me and him", correct: "He and I", options: ["He and I", "Him and me", "Me and he"], rule: "Subject pronouns" },
      { sentence: "Their going home.", error: "Their", correct: "They're", options: ["They're", "There", "Theyre"], rule: "Homophones" },
      { sentence: "Its time to eat.", error: "Its", correct: "It's", options: ["It's", "Its'", "Is"], rule: "Apostrophes" },
      { sentence: "The dogs tail wagged.", error: "dogs", correct: "dog's", options: ["dog's", "dogs'", "dogs"], rule: "Possessive apostrophe" },
      { sentence: "I could of won.", error: "of", correct: "have", options: ["have", "off", "had"], rule: "Could have" },
    ],
    3: [
      { sentence: "Your the best!", error: "Your", correct: "You're", options: ["You're", "Youre", "Yore"], rule: "Homophones" },
      { sentence: "The team have won.", error: "have", correct: "has", options: ["has", "having", "had"], rule: "Collective nouns" },
      { sentence: "Less people came.", error: "Less", correct: "Fewer", options: ["Fewer", "Lesser", "Little"], rule: "Less vs fewer" },
      { sentence: "Who's book is this?", error: "Who's", correct: "Whose", options: ["Whose", "Whom", "Who"], rule: "Who's vs whose" },
      { sentence: "I did good on the test.", error: "good", correct: "well", options: ["well", "better", "best"], rule: "Good vs well" },
    ],
    4: [
      { sentence: "Between you and I...", error: "I", correct: "me", options: ["me", "myself", "us"], rule: "Object pronouns" },
      { sentence: "The data shows that...", error: "shows", correct: "show", options: ["show", "showing", "showed"], rule: "Latin plurals" },
      { sentence: "Each of them have a book.", error: "have", correct: "has", options: ["has", "having", "had"], rule: "Each/every" },
      { sentence: "Neither option are good.", error: "are", correct: "is", options: ["is", "were", "been"], rule: "Neither/either" },
      { sentence: "Lay down and rest.", error: "Lay", correct: "Lie", options: ["Lie", "Lain", "Laid"], rule: "Lie vs lay" },
    ],
  };

  // Generate enemy wave
  const generateWave = useCallback(() => {
    const settings = difficultySettings[difficulty];
    const problems = grammarProblems[Math.min(level, 4)];
    const shuffled = [...problems].sort(() => Math.random() - 0.5);
    const enemyCount = Math.min(3 + wave, 6);
    
    return shuffled.slice(0, enemyCount).map((problem, index) => ({
      id: Date.now() + index,
      ...problem,
      health: 100,
      distance: 100 - (index * 15), // Stagger enemies
      speed: settings.speed + (level * 0.1)
    }));
  }, [level, wave, difficulty]);

  // Start game
  const startGame = useCallback((selectedDifficulty) => {
    if (selectedDifficulty) setDifficulty(selectedDifficulty);
    const settings = difficultySettings[selectedDifficulty || difficulty];
    setGameState('playing');
    setScore(0);
    setLevel(settings.startLevel);
    setHealth(settings.health);
    setWave(1);
    setEnemiesDefeated(0);
    setCombo(0);
    const newEnemies = generateWave();
    setEnemies(newEnemies);
    setCurrentEnemy(newEnemies[0]);
    setSelectedFix(null);
  }, [generateWave, difficulty]);

  // Move enemies closer
  useEffect(() => {
    if (gameState !== 'playing' || !currentEnemy) return;
    
    const interval = setInterval(() => {
      setEnemies(prev => {
        const updated = prev.map(e => ({
          ...e,
          distance: Math.max(0, e.distance - e.speed)
        }));
        
        // Check if any enemy reached castle
        const reached = updated.find(e => e.distance <= 0);
        if (reached) {
          const settings = difficultySettings[difficulty];
          setCastleShake(true);
          setTimeout(() => setCastleShake(false), 500);
          setHealth(h => {
            const newHealth = h - settings.reachDamage;
            if (newHealth <= 0) {
              setGameState('finished');
            }
            return Math.max(0, newHealth);
          });
          toast.error(`Enemy reached the castle! -${settings.reachDamage} HP`);
          // Remove that enemy
          return updated.filter(e => e.id !== reached.id);
        }
        
        return updated;
      });
    }, 100);
    
    return () => clearInterval(interval);
  }, [gameState, currentEnemy]);

  // Update current enemy when enemies change
  useEffect(() => {
    if (enemies.length > 0 && (!currentEnemy || !enemies.find(e => e.id === currentEnemy.id))) {
      setCurrentEnemy(enemies[0]);
      setSelectedFix(null);
    } else if (enemies.length === 0 && gameState === 'playing') {
      // Wave cleared!
      setWave(w => w + 1);
      if (wave % 2 === 0) {
        setLevel(l => Math.min(l + 1, 4));
        toast.success(`Level ${Math.min(level + 1, 4)}! Harder grammar ahead!`);
      }
      toast.success(`Wave ${wave} cleared!`);
      setTimeout(() => {
        const newEnemies = generateWave();
        setEnemies(newEnemies);
        setCurrentEnemy(newEnemies[0]);
      }, 1000);
    }
  }, [enemies, currentEnemy, gameState, wave, level, generateWave]);

  // Attack enemy
  const attackEnemy = () => {
    if (!currentEnemy || !selectedFix) return;
    
    if (selectedFix === currentEnemy.correct) {
      // Correct!
      const basePoints = 30 + (level * 10);
      const comboBonus = combo * 10;
      const totalPoints = basePoints + comboBonus;
      
      setScore(s => s + totalPoints);
      setCombo(c => c + 1);
      setEnemiesDefeated(e => e + 1);
      
      toast.success(
        <div>
          <p>+{totalPoints} points! {combo > 0 && `(${combo}x combo)`}</p>
          <p className="text-xs opacity-75">{currentEnemy.rule}</p>
        </div>
      );
      
      // Remove defeated enemy
      setEnemies(prev => prev.filter(e => e.id !== currentEnemy.id));
      setSelectedFix(null);
    } else {
      // Wrong!
      const settings = difficultySettings[difficulty];
      setCombo(0);
      setHealth(h => {
        const newHealth = h - settings.damage;
        if (newHealth <= 0) {
          setGameState('finished');
        }
        return Math.max(0, newHealth);
      });
      toast.error(`Wrong! The correct fix is "${currentEnemy.correct}"`);
      setSelectedFix(null);
    }
  };

  // Save game on finish
  useEffect(() => {
    if (gameState === 'finished') {
      const saveGame = async () => {
        try {
          await api.post('/games/complete', {
            game_id: 'grammar-guardian',
            score: score,
            time_seconds: 0,
            metadata: { level, wave, enemies_defeated: enemiesDefeated, best_combo: combo }
          });
        } catch (error) {
          console.error('Error saving game:', error);
        }
      };
      saveGame();
    }
  }, [gameState, score, level, wave, enemiesDefeated, combo, api]);

  return (
    <StudentLayout>
      <div data-testid="grammar-guardian-game" className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button 
            onClick={() => navigate('/student/games')}
            className="flex items-center gap-2 text-slate-600 hover:text-purple-600 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Games
          </button>
          
          {gameState === 'playing' && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-red-500" />
                <div className="w-20 h-3 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all ${health > 50 ? 'bg-emerald-500' : health > 25 ? 'bg-amber-500' : 'bg-red-500'}`}
                    style={{ width: `${health}%` }}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 bg-amber-100 px-3 py-1 rounded-full">
                <Star className="w-4 h-4 text-amber-600" />
                <span className="font-bold text-amber-700">{score}</span>
              </div>
            </div>
          )}
        </div>

        {/* Game Title */}
        <div className="student-card bg-gradient-to-r from-red-100 to-orange-100 border-red-300 mb-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-red-500 rounded-2xl border-2 border-gray-800 flex items-center justify-center">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-800">Grammar Guardian</h1>
              <p className="text-slate-600">
                {gameState === 'playing' ? `Wave ${wave} • Level ${level}` : 'Defend the castle with grammar!'}
              </p>
            </div>
          </div>
        </div>

        {/* Ready Screen */}
        {gameState === 'ready' && (
          <div className="student-card text-center py-12 bg-gradient-to-b from-slate-800 to-red-900">
            <div className="w-24 h-24 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Castle className="w-14 h-14 text-white" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-white mb-4">Grammar Guardian</h2>
            <p className="text-lg text-red-200 mb-6">
              Enemies are attacking with bad grammar! Fix their sentences to defeat them!
            </p>
            
            {/* Difficulty Selection */}
            <div className="mb-8">
              <p className="text-red-300 mb-4">Select Difficulty:</p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setDifficulty('easy')}
                  className={`px-6 py-3 rounded-xl font-bold transition-all ${
                    difficulty === 'easy' 
                      ? 'bg-green-500 text-white scale-105' 
                      : 'bg-red-800 text-red-300 hover:bg-red-700'
                  }`}
                >
                  Easy
                  <span className="block text-xs opacity-75">150 HP, slow enemies</span>
                </button>
                <button
                  onClick={() => setDifficulty('medium')}
                  className={`px-6 py-3 rounded-xl font-bold transition-all ${
                    difficulty === 'medium' 
                      ? 'bg-amber-500 text-white scale-105' 
                      : 'bg-red-800 text-red-300 hover:bg-red-700'
                  }`}
                >
                  Medium
                  <span className="block text-xs opacity-75">100 HP, normal speed</span>
                </button>
                <button
                  onClick={() => setDifficulty('hard')}
                  className={`px-6 py-3 rounded-xl font-bold transition-all ${
                    difficulty === 'hard' 
                      ? 'bg-red-600 text-white scale-105' 
                      : 'bg-red-800 text-red-300 hover:bg-red-700'
                  }`}
                >
                  Hard
                  <span className="block text-xs opacity-75">75 HP, fast enemies</span>
                </button>
              </div>
            </div>
            
            <div className="text-red-300 mb-8 space-y-1">
              <p>🏰 Protect your castle</p>
              <p>⚔️ Fix grammar errors to attack</p>
              <p>📚 Learn grammar rules as you play</p>
            </div>
            <Button onClick={() => startGame(difficulty)} className="bg-red-500 hover:bg-red-600 text-white text-lg px-10 py-6">
              Defend Castle
              <Swords className="w-6 h-6 ml-2" />
            </Button>
          </div>
        )}

        {/* Playing Screen */}
        {gameState === 'playing' && currentEnemy && (
          <div className="student-card bg-gradient-to-b from-slate-800 to-red-900 min-h-[450px]">
            {/* Wave & Combo Info */}
            <div className="flex justify-between items-center mb-4">
              <div className="text-red-200">
                <span className="text-xs">WAVE {wave}</span>
                <p className="font-bold">Enemies: {enemies.length}</p>
              </div>
              {combo > 1 && (
                <div className="bg-amber-500/20 px-3 py-1 rounded-full animate-pulse">
                  <span className="text-amber-300 font-bold">{combo}x COMBO!</span>
                </div>
              )}
              <div className="text-red-200 text-right">
                <span className="text-xs">DEFEATED</span>
                <p className="font-bold">{enemiesDefeated}</p>
              </div>
            </div>

            {/* Battle Field */}
            <div className="relative h-32 mb-6 bg-gradient-to-r from-green-900 to-green-800 rounded-lg overflow-hidden">
              {/* Castle */}
              <div className={`absolute right-2 bottom-0 transition-all ${castleShake ? 'animate-shake' : ''}`}>
                <Castle className="w-16 h-20 text-stone-400" />
              </div>
              
              {/* Enemies */}
              {enemies.map((enemy, idx) => (
                <div
                  key={enemy.id}
                  className={`absolute bottom-2 transition-all duration-200 ${enemy.id === currentEnemy.id ? 'scale-110' : 'opacity-70'}`}
                  style={{ left: `${enemy.distance}%`, transform: 'translateX(-50%)' }}
                >
                  <div className="w-12 h-12 bg-red-600 rounded-lg border-2 border-red-400 flex items-center justify-center">
                    <span className="text-2xl">👹</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Current Enemy's Sentence */}
            <div className="bg-slate-900/50 rounded-xl p-4 mb-4">
              <p className="text-red-300 text-sm mb-2">Enemy says:</p>
              <p className="text-xl text-white font-medium">
                "{currentEnemy.sentence.split(currentEnemy.error).map((part, i, arr) => (
                  <React.Fragment key={i}>
                    {part}
                    {i < arr.length - 1 && (
                      <span className="bg-red-500/50 px-1 rounded text-red-200 underline">{currentEnemy.error}</span>
                    )}
                  </React.Fragment>
                ))}"
              </p>
              <p className="text-slate-400 text-sm mt-2">Find and fix the error!</p>
            </div>

            {/* Fix Options */}
            <div className="mb-4">
              <p className="text-red-200 text-sm mb-2">Replace "{currentEnemy.error}" with:</p>
              <div className="flex gap-3 flex-wrap">
                {shuffledOptions.map(option => (
                    <button
                      key={option}
                      onClick={() => setSelectedFix(option)}
                      className={`px-4 py-2 rounded-lg font-bold transition-all ${
                        selectedFix === option
                          ? 'bg-emerald-500 text-white scale-105'
                          : 'bg-slate-700 text-white hover:bg-slate-600'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
              </div>
            </div>

            {/* Attack Button */}
            <Button
              onClick={attackEnemy}
              disabled={!selectedFix}
              className="w-full bg-red-500 hover:bg-red-600 text-white py-4 text-lg disabled:opacity-50"
            >
              <Swords className="w-5 h-5 mr-2" />
              Attack!
            </Button>
          </div>
        )}

        {/* Finished Screen */}
        {gameState === 'finished' && (
          <div className="student-card text-center py-12 bg-gradient-to-b from-slate-800 to-red-900">
            <div className="w-20 h-20 bg-amber-400 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <Trophy className="w-12 h-12 text-white" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-white mb-2">Castle Defended!</h2>
            <p className="text-xl text-red-200 mb-4">
              You reached Wave {wave}!
            </p>
            
            <div className="grid grid-cols-2 gap-4 my-8 max-w-xs mx-auto">
              <div className="bg-slate-700/50 rounded-xl p-4">
                <p className="text-3xl font-bold text-emerald-400">{enemiesDefeated}</p>
                <p className="text-sm text-red-200">Defeated</p>
              </div>
              <div className="bg-slate-700/50 rounded-xl p-4">
                <p className="text-3xl font-bold text-amber-400">{wave}</p>
                <p className="text-sm text-red-200">Waves</p>
              </div>
            </div>
            
            <div className="flex items-center justify-center gap-2 mb-8">
              <Star className="w-8 h-8 text-amber-400 fill-amber-400" />
              <span className="font-heading text-4xl font-bold text-amber-400">+{score}</span>
              <span className="text-red-200 text-xl">points</span>
            </div>
            
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={() => startGame()} className="border-red-400 text-red-200 hover:bg-red-800">
                <RotateCcw className="w-4 h-4 mr-2" />
                Defend Again
              </Button>
              <Button onClick={() => navigate('/student/games')} className="bg-red-500 hover:bg-red-600">
                More Games
              </Button>
            </div>
          </div>
        )}
      </div>
    </StudentLayout>
  );
};

export default GrammarGuardianGame;
